// Persisted authentication session backed by localStorage.
// Access-token consumers keep reading `token`; refresh credentials and expiry
// are stored beside it so a session can be renewed without losing identity.
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { getSafeStorage } from '@/lib/storage'
import type { AuthUser } from '@/lib/types'

export type AuthSession = {
  token: string
  refreshToken: string
  expiresAt: number | null
  user: AuthUser
  sessionId: string
}

type AuthState = {
  token: string | null
  refreshToken: string | null
  expiresAt: number | null
  user: AuthUser | null
  sessionId: string | null
  setSession: (
    token: string,
    user: AuthUser,
    refreshToken?: string | null,
    expiresAt?: number | null,
  ) => void
  rotateSession: (
    expectedSessionId: string,
    expectedRefreshToken: string,
    session: Omit<AuthSession, 'sessionId'>,
  ) => boolean
  clear: () => void
}

const STORAGE_KEY = 'livevane.auth'

function createSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // Fall through for restricted browser contexts.
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** Convert either a Unix timestamp or a relative lifetime to milliseconds. */
export function resolveAccessExpiry(
  expiresIn?: number,
  expiresAt?: number,
): number | null {
  if (typeof expiresAt === 'number' && Number.isFinite(expiresAt) && expiresAt > 0) {
    return expiresAt < 1_000_000_000_000 ? expiresAt * 1000 : expiresAt
  }
  if (typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0) {
    return Date.now() + expiresIn * 1000
  }
  return null
}

function migratePersistedState(value: unknown): Partial<AuthState> {
  if (!value || typeof value !== 'object') return {}
  const state = value as Partial<AuthState>
  const token = typeof state.token === 'string' && state.token !== '' ? state.token : null
  if (!token) {
    return {
      token: null,
      user: null,
      refreshToken: null,
      expiresAt: null,
      sessionId: null,
    }
  }

  const user = state.user && typeof state.user === 'object' ? state.user : null
  const refreshToken =
    typeof state.refreshToken === 'string' && state.refreshToken !== ''
      ? state.refreshToken
      : null
  const expiresAt =
    typeof state.expiresAt === 'number' && Number.isFinite(state.expiresAt)
      ? state.expiresAt
      : null
  const sessionId =
    typeof state.sessionId === 'string' && state.sessionId !== ''
      ? state.sessionId
      : createSessionId()

  return { token, user, refreshToken, expiresAt, sessionId }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      sessionId: null,
      setSession: (token, user, refreshToken = null, expiresAt = null) =>
        set({
          token,
          user,
          refreshToken,
          expiresAt,
          sessionId: createSessionId(),
        }),
      rotateSession: (expectedSessionId, expectedRefreshToken, session) => {
        let updated = false
        set((state) => {
          if (
            state.sessionId !== expectedSessionId ||
            state.refreshToken !== expectedRefreshToken
          ) {
            return state
          }
          updated = true
          return { ...session, sessionId: expectedSessionId }
        })
        return updated
      },
      clear: () =>
        set({
          token: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
          sessionId: null,
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => getSafeStorage()),
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        user: state.user,
        sessionId: state.sessionId,
      }),
      version: 2,
      migrate: (persistedState) =>
        migratePersistedState(persistedState) as AuthState,
    },
  ),
)

// Existing selectors intentionally keep their original contracts.
export const useAuthToken = (): string | null =>
  useAuthStore((s) => s.token)

export const useAuthUser = (): AuthUser | null =>
  useAuthStore((s) => s.user)

export const useIsAuthenticated = (): boolean =>
  useAuthStore((s) => s.token !== null)

export const AUTH_STORAGE_KEY = STORAGE_KEY
